import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { templateService } from '../services/templateService'
import { TemplateFormData, TemplateCategory } from '../types/wordTemplate'
import './TemplateForm.css'

export function TemplateEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    description: '',
    category: 'letter',
    s3Key: '',
    fileName: '',
    fileSize: 0,
  })
  const [file, setFile] = useState<File | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const { data: template, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id!),
    enabled: !!id,
  })

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description || '',
        category: template.category,
        s3Key: template.s3Key,
        fileName: template.fileName,
        fileSize: template.fileSize,
        format: template.format,
        mimeType: template.mimeType,
      })
    }
  }, [template])

  const updateMutation = useMutation({
    mutationFn: (data: Partial<TemplateFormData>) => templateService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      queryClient.invalidateQueries({ queryKey: ['template', id] })
      navigate(`/templates/${id}`)
    },
  })

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    setIsUploading(true)
    if (!selectedFile.name.endsWith('.odt') && !selectedFile.name.endsWith('.ott')) {
      setUploadError('Please select an .odt or .ott file')
      setIsUploading(false)
      return
    }

    if (selectedFile.size > 25 * 1024 * 1024) {
      setUploadError('File size must be less than 25MB')
      setIsUploading(false)
      return
    }

    setFile(selectedFile)
    setUploadError(null)
    setFormData((prev) => ({
      ...prev,
      s3Key: '',
      fileName: '',
      fileSize: 0,
      templateId: undefined,
      format: undefined,
      mimeType: undefined,
    }))

    try {
      const initResponse = await templateService.initUpload(selectedFile)
      await templateService.uploadToS3(initResponse.uploadUrl, initResponse.fields, selectedFile)
      setFormData((prev) => ({
        ...prev,
        templateId: initResponse.templateId,
        s3Key: initResponse.s3Key,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        format: selectedFile.name.endsWith('.odt') ? 'odt' : 'ott',
        mimeType: selectedFile.type || undefined,
      }))
    } catch (error) {
      console.error('[TemplateEdit] Error uploading file:', error)
      setUploadError('Error uploading file: ' + String(error))
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      alert('Please enter a template name')
      return
    }

    try {
      // Build update data
      const updateData: Partial<TemplateFormData> = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
      }
      
      // If a new file was uploaded, include the new content
      // Check if content has changed from the original template content
      const contentChanged = file && formData.s3Key && formData.s3Key !== template?.s3Key
      
      if (contentChanged) {
        updateData.s3Key = formData.s3Key
        updateData.fileName = formData.fileName
        updateData.fileSize = formData.fileSize
        updateData.format = formData.format
        updateData.mimeType = formData.mimeType
      } else if (!file) {
        console.log('[TemplateEdit] No new file uploaded, keeping existing content')
      } else {
        console.log('[TemplateEdit] File selected but content may not be loaded yet')
        alert('Please wait for the file to finish uploading before submitting')
        return
      }
      
      if (isUploading) {
        alert('Please wait for the file upload to finish before submitting')
        return
      }

      const updated = await updateMutation.mutateAsync(updateData)
      if (!updated?.id) {
        alert('Template update completed but response was invalid. Please refresh and try again.')
      }
    } catch (error: any) {
      console.error('[TemplateEdit] Update error:', error)
      alert(error?.response?.data?.message || error?.message || 'Failed to update template')
    }
  }

  const categoryOptions: { value: TemplateCategory; label: string }[] = [
    { value: 'letter', label: 'Letter' },
    { value: 'form', label: 'Form' },
    { value: 'referral', label: 'Referral' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'report', label: 'Report' },
    { value: 'other', label: 'Other' },
  ]

  if (isLoading) {
    return <div className="template-form-page">Loading template...</div>
  }

  return (
    <div className="template-form-page">
      <div className="template-form-header">
        <h1>Edit Template</h1>
        <button className="btn-secondary" onClick={() => navigate(`/templates/${id}`)}>
          Cancel
        </button>
      </div>

      <form className="template-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Template Name *</label>
          <input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Referral Letter Template"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe what this template is used for..."
            rows={3}
          />
        </div>

        <div className="form-group">
          <label htmlFor="category">Category *</label>
          <select
            id="category"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as TemplateCategory })}
            required
          >
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="file">OpenOffice Template File (.odt/.ott)</label>
          <input
            id="file"
            type="file"
            accept=".odt,.ott"
            onChange={handleFileChange}
          />
          {file && (
            <div className="file-info">
              <span>✓ {file.name} ({(file.size / 1024).toFixed(2)} KB)</span>
            </div>
          )}
          {!file && template && (
            <div className="file-info">
              <span>Current template file is stored. Upload a new file to replace it.</span>
            </div>
          )}
          {uploadError && <div className="error-message">{uploadError}</div>}
          <div className="form-help">
            Upload a new OpenOffice document (.odt/.ott) to replace the existing template.
            <br />
            Leave empty to keep the current template file.
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/templates/${id}`)}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={updateMutation.isPending || isUploading}>
            {isUploading ? 'Uploading...' : updateMutation.isPending ? 'Updating...' : 'Update Template'}
          </button>
        </div>
      </form>
    </div>
  )
}
































